import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Customer,
  CustomerViewModel,
  CustomerListResponse,
  CountryLookup,
  CityLookup,
  ApiResponse,
} from '@/api/types'

/**
 * Customer management service
 * Handles all customer-related API operations
 */
export class CustomersService {
  /**
   * Get paginated list of customers for a branch
   */
  async getAllCustomers(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<CustomerListResponse> {
    const url = endpoints.customers.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<CustomerListResponse> | CustomerListResponse | any>(url)
    const payload = response.data

    // Helper to map customers array
    const mapCustomers = (customers: any[]): Customer[] => {
      return customers.map(cust => this.mapCustomerData(cust))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapCustomers(data),
          }
        }
        return data as CustomerListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapCustomers(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapCustomers(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapCustomers(payload.data) : [],
    }
  }

  /**
   * Get customer by ID
   */
  async getCustomerById(branchId: number, customerId: number): Promise<Customer> {
    const url = endpoints.customers.getById(branchId, customerId)
    const response = await apiClient.get<ApiResponse<Customer> | Customer | any>(url)
    
    let customerData: any
    
    if ('data' in response.data && 'success' in response.data) {
      customerData = response.data.data
    } else if ('data' in response.data) {
      customerData = response.data.data
    } else {
      customerData = response.data
    }
    
    return this.mapCustomerData(customerData)
  }

  /**
   * Save customer (insert)
   */
  async saveCustomer(customer: CustomerViewModel): Promise<Customer> {
    const url = endpoints.customers.save
    const response = await apiClient.post<ApiResponse<Customer> | Customer>(url, customer)
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapCustomerData((response.data as ApiResponse<Customer>).data)
    }
    
    return this.mapCustomerData(response.data as Customer)
  }

  /**
   * Update customer
   */
  async updateCustomer(customer: CustomerViewModel): Promise<Customer> {
    const url = endpoints.customers.update
    const response = await apiClient.put<ApiResponse<Customer> | Customer>(url, customer)
    
    if ('data' in response.data && 'success' in response.data) {
      return this.mapCustomerData((response.data as ApiResponse<Customer>).data)
    }
    
    return this.mapCustomerData(response.data as Customer)
  }

  /**
   * Get user IP address (logged-in user's IP, not server's IP)
   */
  private async getUserIPAddress(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  /**
   * Delete customer
   */
  async deleteCustomer(customerId: number, branchId: number, ipAddress?: string): Promise<void> {
    // Get IP address if not provided (this gets the logged-in user's IP, not server IP)
    const userIP = ipAddress || await this.getUserIPAddress()
    const url = endpoints.customers.delete(customerId, branchId, userIP)
    await apiClient.delete(url)
  }

  /**
   * Get country lookups
   */
  async getCountries(): Promise<CountryLookup[]> {
    const url = endpoints.customers.getCountries
    const response = await apiClient.get<ApiResponse<CountryLookup[]> | CountryLookup[] | any>(url)
    
    let countriesData: any[] = []
    
    if (Array.isArray(response.data)) {
      countriesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      countriesData = response.data.data
    }
    
    // Map to CountryLookup format
    return countriesData.map((item: any) => ({
      id: item.id || item.countryId || 0,
      lookupNameEn: item.lookupNameEn || item.countryNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.countryNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get city lookups
   */
  async getCities(countryId?: number): Promise<CityLookup[]> {
    const url = endpoints.customers.getCities(countryId)
    const response = await apiClient.get<ApiResponse<CityLookup[]> | CityLookup[] | any>(url)
    
    let citiesData: any[] = []
    
    if (Array.isArray(response.data)) {
      citiesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      citiesData = response.data.data
    }
    
    // Map to CityLookup format
    return citiesData.map((item: any) => ({
      id: item.id || item.cityId || 0,
      lookupNameEn: item.lookupNameEn || item.cityNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.cityNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get state lookups
   */
  async getStates(): Promise<CityLookup[]> {
    const url = endpoints.customers.getStates
    const response = await apiClient.get<ApiResponse<CityLookup[]> | CityLookup[] | any>(url)
    
    let statesData: any[] = []
    
    if (Array.isArray(response.data)) {
      statesData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      statesData = response.data.data
    }
    
    // Map to CityLookup format (states use same structure as cities)
    return statesData.map((item: any) => ({
      id: item.id || item.stateId || 0,
      lookupNameEn: item.lookupNameEn || item.stateNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.stateNameAr || item.nameAr || '',
    }))
  }

  /**
   * Map API response to Customer interface
   */
  private mapCustomerData(customerData: any): Customer {
    return {
      customerId: customerData.customerId || customerData.id || 0,
      id: customerData.id || customerData.customerId || 0,
      customerNameEn: customerData.customerNameEn || customerData.nameEn || customerData.name || '',
      customerNameAr: customerData.customerNameAr || customerData.nameAr || '',
      email: customerData.email || '',
      phone: customerData.phone || customerData.phoneNo || '',
      phoneNo: customerData.mobileNo || customerData.mobileNo || '',
      faxNo: customerData.faxNo || '',
      mobileNo: customerData.mobileNo || customerData.mobile || '',
      website: customerData.website || '',
      customerType: customerData.customerType || 0,
      primaryPerson: customerData.primaryPerson || customerData.contactPerson || '',
      isUoMApplicable: customerData.isUom !== undefined ? customerData.isUom : (customerData.isUoMApplicable !== undefined ? customerData.isUoMApplicable : false),
      isUom: customerData.isUom !== undefined ? customerData.isUom : (customerData.isUoMApplicable !== undefined ? customerData.isUoMApplicable : false),
      country: customerData.country || 0,
      countryAr: customerData.countryAr || '',
      city: customerData.city || 0,
      cityAr: customerData.cityAr || '',
      state: customerData.state || customerData.city || 0,
      street: customerData.street || '',
      streetAr: customerData.streetAr || '',
      citySubdivision: customerData.citySubdivision || '',
      citySubdivisionAr: customerData.citySubdivisionAr || '',
      zipCode: customerData.zipCode || customerData.postalCode || '',
      plotNumber: customerData.plotNumber || (customerData.plot ? String(customerData.plot) : '') || '',
      plot: customerData.plot || (customerData.plotNumber ? Number(customerData.plotNumber) : 0) || 0,
      buildingNumber: customerData.buildingNumber ? String(customerData.buildingNumber) : '',
      address: customerData.address || customerData.addressEn || '',
      addressAr: customerData.addressAr || '',
      addressar: customerData.addressar || customerData.addressAr || '',
      companyRegistrationNumber: customerData.companyRegistrationNumber || customerData.companyRegistrationNo || customerData.companyRegNumber || '',
      companyRegistrationNo: customerData.companyRegistrationNo || customerData.companyRegistrationNumber || customerData.companyRegNumber || '',
      iqamaNo: customerData.iqamaNo || customerData.iqamaNumber || '',
      vatNo: customerData.vatNo || customerData.vatNumber || '',
      activeStatus: customerData.activeStatus !== undefined ? customerData.activeStatus : (customerData.isActive !== undefined ? customerData.isActive : true),
      isActive: customerData.isActive !== undefined ? customerData.isActive : (customerData.activeStatus !== undefined ? customerData.activeStatus : true),
      branchId: customerData.branchId,
      companyId: customerData.companyId,
      chartOfAccountId: customerData.chartOfAccountId || customerData.accountId || 0,
    } as Customer
  }
}

export const customersService = new CustomersService()
